import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider, useI18n } from "@/i18n/I18nContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { PublicLayout } from "@/components/PublicLayout";
import { AdminLayout } from "@/pages/admin/AdminLayout";

import Home from "@/pages/public/Home";
import Services from "@/pages/public/Services";
import ServiceDetail from "@/pages/public/ServiceDetail";
import Branches from "@/pages/public/Branches";
import Gallery from "@/pages/public/Gallery";
import Booking, { BookingConfirmed } from "@/pages/public/Booking";
import Shop from "@/pages/public/Shop";
import ProductDetail from "@/pages/public/ProductDetail";
import Cart from "@/pages/public/Cart";
import Memberships from "@/pages/public/Memberships";
import Gifts from "@/pages/public/Gifts";
import Contact from "@/pages/public/Contact";
import FAQ from "@/pages/public/FAQ";
import Policies from "@/pages/public/Policies";
import Login from "@/pages/auth/Login";
import Account from "@/pages/account/Account";

import Overview from "@/pages/admin/Overview";
import AdminBookings from "@/pages/admin/Bookings";
import Cashier from "@/pages/admin/Cashier";
import Integrations from "@/pages/admin/Integrations";
import ContentEditor from "@/pages/admin/ContentEditor";
import StaffPortal from "@/pages/staff/StaffPortal";
import { ServicesAdmin, BranchesAdmin, StaffAdmin } from "@/pages/admin/catalog";
import { Customers, Payments, Complaints, Reports, Roles, Audit, Settings, Loyalty } from "@/pages/admin/modules";
import { Attendance, InventoryPro } from "@/pages/admin/staff_ops";

const Pub = (C) => (<PublicLayout>{C}</PublicLayout>);

function StaffRoute({ children }) {
  const { user, loading, isStaff } = useAuth();
  const { t } = useI18n();
  if (loading) return <div className="flex min-h-screen items-center justify-center text-slate-400">{t("common.loading")}</div>;
  if (!user || !isStaff) return <Navigate to="/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function Shell() {
  const { dir } = useI18n();
  return (
    <>
      <Toaster position={dir === "rtl" ? "top-left" : "top-right"} richColors />
      <Routes>
        <Route path="/" element={Pub(<Home />)} />
        <Route path="/services" element={Pub(<Services />)} />
        <Route path="/services/:id" element={Pub(<ServiceDetail />)} />
        <Route path="/branches" element={Pub(<Branches />)} />
        <Route path="/gallery" element={Pub(<Gallery />)} />
        <Route path="/booking" element={Pub(<Booking />)} />
        <Route path="/booking/confirmed/:id" element={Pub(<BookingConfirmed />)} />
        <Route path="/shop" element={Pub(<Shop />)} />
        <Route path="/shop/:id" element={Pub(<ProductDetail />)} />
        <Route path="/cart" element={Pub(<Cart />)} />
        <Route path="/memberships" element={Pub(<Memberships />)} />
        <Route path="/gifts" element={Pub(<Gifts />)} />
        <Route path="/contact" element={Pub(<Contact />)} />
        <Route path="/faq" element={Pub(<FAQ />)} />
        <Route path="/policies" element={Pub(<Policies />)} />
        <Route path="/policies/:key" element={Pub(<Policies />)} />
        <Route path="/login" element={<Login />} />
        <Route path="/account" element={Pub(<Account />)} />

        <Route path="/admin" element={<StaffRoute><Overview /></StaffRoute>} />
        <Route path="/admin/content" element={<StaffRoute><ContentEditor /></StaffRoute>} />
        <Route path="/admin/bookings" element={<StaffRoute><AdminBookings /></StaffRoute>} />
        <Route path="/admin/customers" element={<StaffRoute><Customers /></StaffRoute>} />
        <Route path="/admin/services" element={<StaffRoute><ServicesAdmin /></StaffRoute>} />
        <Route path="/admin/branches" element={<StaffRoute><BranchesAdmin /></StaffRoute>} />
        <Route path="/admin/staff" element={<StaffRoute><StaffAdmin /></StaffRoute>} />
        <Route path="/admin/payments" element={<StaffRoute><Payments /></StaffRoute>} />
        <Route path="/admin/cashier" element={<StaffRoute><Cashier /></StaffRoute>} />
        <Route path="/admin/inventory" element={<StaffRoute><InventoryPro /></StaffRoute>} />
        <Route path="/admin/attendance" element={<StaffRoute><Attendance /></StaffRoute>} />
        <Route path="/staff" element={<StaffRoute><StaffPortal /></StaffRoute>} />
        <Route path="/admin/loyalty" element={<StaffRoute><Loyalty /></StaffRoute>} />
        <Route path="/admin/complaints" element={<StaffRoute><Complaints /></StaffRoute>} />
        <Route path="/admin/reports" element={<StaffRoute><Reports /></StaffRoute>} />
        <Route path="/admin/roles" element={<StaffRoute><Roles /></StaffRoute>} />
        <Route path="/admin/integrations" element={<StaffRoute><Integrations /></StaffRoute>} />
        <Route path="/admin/audit" element={<StaffRoute><Audit /></StaffRoute>} />
        <Route path="/admin/settings" element={<StaffRoute><Settings /></StaffRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
